import { StandardPageLayout } from "@/components/layouts/standard-page-layout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ConfirmationModal from "components/modals/confirmation-modal";
import { LetterAvatar } from "components/shared/letter-avatar";
import {
  GetUserIfLoggedIn,
  getActiveOrganisation,
  useIdentity,
} from "contexts/identity-context";
import * as QueryKeys from "data";
import { UserMembership } from "data/models";
import {
  userAcceptInvitation,
  userRejectInvitation,
} from "data/mutations-common";
import { fetchUserInvitations } from "data/queries-common";
import { MailCheck } from "lucide-react";
import { useState } from "react";
import { resolveIdentityId } from "utils/identity";

export const UserInvitations = () => {
  const [identity, setIdentity] = useIdentity();
  const identityId = resolveIdentityId(identity);

  const queryClient = useQueryClient();

  const activeOrg = getActiveOrganisation(identity);

  const userInvitationsQuery = useQuery({
    queryKey: [QueryKeys.userInvitations, identityId],
    queryFn: fetchUserInvitations,
  });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] =
    useState<UserMembership | null>(null);

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedInvitation(null);
  };

  const handleDelete = (invitation: UserMembership) => {
    setSelectedInvitation(invitation);
    setDeleteOpen(true);
  };

  const acceptInvitationMutation = useMutation({
    mutationFn: (invitation: UserMembership) =>
      userAcceptInvitation(invitation.organisationId),
    onSuccess: (_, invitation) => {
      queryClient.setQueryData<UserMembership[]>(
        [QueryKeys.userInvitations, identityId],
        (oldData) => {
          if (!oldData) return [];
          return oldData.filter(
            (inv) => inv.organisationId !== invitation.organisationId,
          );
        },
      );

      setIdentity((prevIdentity) => {
        const user = GetUserIfLoggedIn(prevIdentity);
        if (user) {
          return {
            ...user,
            organisations: [
              ...user.organisations,
              {
                name: invitation.organisationName,
                organisationId: invitation.organisationId,
                active: false,
              },
            ],
          };
        }
        return prevIdentity;
      });
    },
  });

  const rejectInvitationMutation = useMutation({
    mutationFn: (invitation: UserMembership) =>
      userRejectInvitation(invitation.organisationId),
    onSuccess: (_, invitation) => {
      queryClient.setQueryData<UserMembership[]>(
        [QueryKeys.userInvitations, identityId],
        (oldData) => {
          if (!oldData) return [];
          return oldData.filter(
            (inv) => inv.organisationId !== invitation.organisationId,
          );
        },
      );
      handleDeleteClose();
    },
  });

  const pendingInvitations = (userInvitationsQuery.data || [])
    .filter((invitation) => invitation.status === "Pending")
    .sort((a, b) =>
      a.organisationName
        .toUpperCase()
        .localeCompare(b.organisationName.toUpperCase()),
    );

  return (
    <StandardPageLayout
      pageTitle={{
        icon: MailCheck,
        title: "Pending Invitations",
        description:
          "Review and respond to organisation invitations you've received.",
      }}
    >
      <div className="mx-auto max-w-5xl space-y-4">
        {acceptInvitationMutation.isSuccess && (
          <Alert variant="success">
            <AlertDescription>
              Invitation from "
              {acceptInvitationMutation.variables?.organisationName}" has been
              accepted
            </AlertDescription>
          </Alert>
        )}

        {acceptInvitationMutation.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {(acceptInvitationMutation.error as Error)?.message ||
                "An error occurred while accepting the invitation"}
            </AlertDescription>
          </Alert>
        )}

        {rejectInvitationMutation.isSuccess && (
          <Alert variant="success">
            <AlertDescription>Invitation has been rejected</AlertDescription>
          </Alert>
        )}

        {rejectInvitationMutation.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {(rejectInvitationMutation.error as Error)?.message ||
                "An error occurred while rejecting the invitation"}
            </AlertDescription>
          </Alert>
        )}

        {userInvitationsQuery.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              An error occurred while fetching the invitations
            </AlertDescription>
          </Alert>
        )}

        <ConfirmationModal
          isLoading={rejectInvitationMutation.isPending}
          isError={rejectInvitationMutation.isError}
          open={deleteOpen}
          header={`Reject this invitation?`}
          body={`You won't be able to join "${selectedInvitation?.organisationName}" again unless you receive another invitation.`}
          leftButtonText={"CANCEL"}
          leftButtonColor="primary"
          rightButtonText={"REJECT INVITATION"}
          rightButtonColor="error"
          handleClose={() => handleDeleteClose()}
          handleLeftButton={() => handleDeleteClose()}
          handleRightButton={() => {
            if (selectedInvitation) {
              rejectInvitationMutation.mutate(selectedInvitation);
            }
          }}
        ></ConfirmationModal>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-sm font-semibold">
                Organisation Name
              </TableHead>
              <TableHead className="text-sm font-semibold text-right">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {userInvitationsQuery.isPending ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="flex items-center gap-5">
                      <Skeleton className="size-10 rounded" />
                      <Skeleton className="h-4 w-40" />
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Skeleton className="ml-auto h-8 w-40" />
                  </TableCell>
                </TableRow>
              ))
            ) : pendingInvitations.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={2}
                  className="text-muted-foreground text-center"
                >
                  No pending invitations
                </TableCell>
              </TableRow>
            ) : (
              pendingInvitations.map((invitation) => (
                <TableRow key={invitation.organisationName}>
                  <TableCell>
                    <div className="flex items-center gap-2 text-base">
                      <LetterAvatar
                        name={invitation.organisationName}
                        isOrg={!!activeOrg}
                        isLoading={userInvitationsQuery.isPending}
                      />
                      {invitation.organisationName}
                    </div>
                  </TableCell>
                  <TableCell className="">
                    <div className="flex justify-end gap-2">
                      <Button
                        disabled={acceptInvitationMutation.isPending}
                        onClick={() =>
                          acceptInvitationMutation.mutate(invitation)
                        }
                      >
                        {acceptInvitationMutation.isPending && <Spinner />}
                        Accept
                      </Button>
                      <Button
                        variant="destructive"
                        disabled={rejectInvitationMutation.isPending}
                        onClick={() => handleDelete(invitation)}
                      >
                        {rejectInvitationMutation.isPending && <Spinner />}
                        Reject
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </StandardPageLayout>
  );
};
